import { useState } from "react";
import { Link } from "react-router-dom";
import VideoModal from "@/components/Modals/VideoModal";
import VideoPlayer from "@/components/Modals/VideoModal/VideoPlayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  isYouTubeUrl,
  getYouTubeId,
  getYouTubeEmbedUrl,
  getYouTubeThumbnail,
} from "@/utils/youtubeUtils";

export default function CarouselItem({
  item,
  playPauseCarousel,
  changePlayPauseCarousel,
}) {
  const [open, setOpen] = useState(false);

  const isYT = item.mediaType === "video" && isYouTubeUrl(item.mediaSrc);
  const ytId = isYT ? getYouTubeId(item.mediaSrc) : null;
  const ytThumbnail = ytId ? getYouTubeThumbnail(ytId) : null;

  const ctaClassName =
    "text-xs sm:text-base bg-aw-green text-aw-bluedark font-semibold px-3 sm:px-6 py-3 rounded-3xl hover:bg-aw-green-dark transition duration-300 scale-100 hover:scale-95 flex items-center justify-center";

  const renderCTA = () => {
    if (!item.botonTexto || item.botonTipoUrl === "ninguno") return null;
    if (item.botonTipoUrl === "interno") {
      return (
        <Link to={item.botonUrl} className={ctaClassName}>
          <p className="m-0 p-0 line-clamp-1">{item.botonTexto}</p>
        </Link>
      );
    }
    return (
      <a
        href={item.botonUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={ctaClassName}
      >
        <p className="m-0 p-0 line-clamp-1">{item.botonTexto}</p>
      </a>
    );
  };

  const handleOpenVideo = () => {
    if (playPauseCarousel) changePlayPauseCarousel();
    setOpen(true);
  };

  return (
    <div
      key={item.id}
      className="min-w-full h-full relative flex items-center justify-center overflow-hidden transition-transform duration-300 ease-in-out border-2 border-aw-green rounded-lg"
    >
      {/* Imagen de fondo */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${item.backgroundSrc})` }}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 h-full w-full bg-black/60 backdrop-blur-[0.5px]">
        {/* Columna izquierda — contenido textual */}
        <div className="col-span-1 flex flex-col items-start justify-center relative z-10 text-left select-none text-white px-8 2xl:px-16">
          {item.verified && (
            <span className="text-sm md:text-lg font-semibold mb-2 animate-fade-in">
              <FontAwesomeIcon
                icon="fa-solid fa-check-circle"
                className="mr-2 text-sky-500 bg-white rounded-full"
              />
              Cuenta Verificada
            </span>
          )}
          <h2 className="line-clamp-1 text-3xl md:text-5xl font-bold sm:mb-4 animate-fade-in">
            {item.name}
          </h2>
          <p className="md:text-xl line-clamp-1 opacity-90 animate-slide-up">
            {item.location + " " + (item.categories || []).join(", ")}
          </p>
          <div className="flex items-center justify-center gap-x-2 sm:mt-4 animate-fade-in">
            {renderCTA()}
            {item.mediaType === "video" && (
              <button
                onClick={handleOpenVideo}
                className="bg-white/80 sm:hidden text-xs sm:text-base text-aw-bluedark flex justify-center items-center gap-x-2 p-3 rounded-full hover:bg-white/70 transition duration-300"
              >
                <FontAwesomeIcon icon="fa-solid fa-play" size="1x" />
              </button>
            )}
          </div>
        </div>

        {/* Columna derecha — media */}
        <div
          className="hidden sm:col-span-1 sm:flex items-center justify-center text-center relative z-10"
          onClick={() => playPauseCarousel && changePlayPauseCarousel()}
        >
          {item.mediaType === "image" && (
            <img
              src={item.mediaSrc}
              alt={item.title}
              className="h-64 mt-4 w-full max-w-md object-contain rounded-lg"
            />
          )}

          {item.mediaType === "video" && (
            <div className="relative w-full max-w-md">
              {/* Thumbnail — YouTube o frame del video directo */}
              {isYT ? (
                <img
                  src={ytThumbnail}
                  alt={item.name}
                  className="h-64 mt-4 w-full max-w-md object-cover rounded-lg"
                />
              ) : (
                <video
                  autoPlay={false}
                  src={item.mediaSrc}
                  className="h-64 mt-4 w-full max-w-md object-contain rounded-lg"
                />
              )}
              {/* Botón play centrado */}
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenVideo();
                  }}
                  className="bg-white/50 text-aw-bluedark p-3 rounded-full hover:bg-white/70 transition duration-300"
                >
                  <FontAwesomeIcon icon="fa-solid fa-play" size="2x" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de video */}
      <VideoModal open={open} onClose={() => setOpen(false)}>
        {isYT ? (
          <iframe
            src={getYouTubeEmbedUrl(item.mediaSrc, true)}
            title={item.name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full aspect-video"
            style={{ border: "none" }}
          />
        ) : (
          <VideoPlayer src={item.mediaSrc} />
        )}
      </VideoModal>
    </div>
  );
}
